import { Breadcrumb, Divider, Layout, theme, Row, Col, Spin, Alert, DatePicker } from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, subDays } from 'date-fns';
import dayjs from "dayjs";
import StatisticsLineChart from './components/StatisticsLineChart';
import StatsSummaryCard from './components/StatsSummaryCard';
import StatisticsTable from './components/StatisticsTable';
import { 
  apiAccountSnapshots, 
  apiDailyTransactions,
  apiAccountsByType,
  apiAccountsByMcc,
  apiAccountsByCategory,
  apiAccountsBySplitPlan,
  apiAccountsByDistributorN1,
  apiAccountsByDistributorN2,
  apiAccountsByAgent
} from '../../../api/statistics';
import './Statistics.scss';

const Statistics = () => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  
  // Date range state - default to last 30 days
  const [dateRange, setDateRange] = useState([
    dayjs(subDays(new Date(), 30)), 
    dayjs(new Date())
  ]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states for numeric endpoints (line graphs)
  const [accountSnapshots, setAccountSnapshots] = useState({ current: [], previous: [] });
  const [transactionData, setTransactionData] = useState({ current: [], previous: [] });
  
  // Data states for list endpoints (tables)
  const [accountsByType, setAccountsByType] = useState({ current: [], previous: [] });
  const [accountsByMcc, setAccountsByMcc] = useState({ current: [], previous: [] });
  const [accountsByCategory, setAccountsByCategory] = useState({ current: [], previous: [] });
  const [accountsBySplitPlan, setAccountsBySplitPlan] = useState({ current: [], previous: [] });
  const [accountsByDistributorN1, setAccountsByDistributorN1] = useState({ current: [], previous: [] });
  const [accountsByDistributorN2, setAccountsByDistributorN2] = useState({ current: [], previous: [] });
  const [accountsByAgent, setAccountsByAgent] = useState({ current: [], previous: [] });

  // Calculate previous period based on current period
  const periodDuration = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return 0;
    return dateRange[1].diff(dateRange[0], 'day');
  }, [dateRange]);

  const previousPeriod = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return [null, null];
    const duration = periodDuration;
    const prevEnd = dateRange[0].subtract(1, 'day');
    const prevStart = prevEnd.subtract(duration, 'day');
    return [prevStart, prevEnd];
  }, [dateRange, periodDuration]);

  // Format date for API
  const formatDateForApi = (date) => {
    return format(date.toDate(), 'yyyy-MM-dd');
  };

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    if (!dateRange[0] || !dateRange[1]) return;
    
    setLoading(true);
    setError(null);
    
    const currentFilters = {
      date_gte: formatDateForApi(dateRange[0]),
      date_lte: formatDateForApi(dateRange[1])
    };
    
    const previousFilters = {
      date_gte: formatDateForApi(previousPeriod[0]),
      date_lte: formatDateForApi(previousPeriod[1])
    };
    
    try {
      // Fetch all endpoints in parallel
      const [
        // Current period
        currentSnapshots,
        currentTransactions,
        currentByType,
        currentByMcc,
        currentByCategory,
        currentBySplitPlan,
        currentByDistN1,
        currentByDistN2,
        currentByAgent,
        // Previous period
        previousSnapshots,
        previousTransactions,
        previousByType,
        previousByMcc,
        previousByCategory,
        previousBySplitPlan,
        previousByDistN1,
        previousByDistN2,
        previousByAgent
      ] = await Promise.all([
        // Current period requests
        apiAccountSnapshots(currentFilters),
        apiDailyTransactions(currentFilters),
        apiAccountsByType(currentFilters),
        apiAccountsByMcc(currentFilters),
        apiAccountsByCategory(currentFilters),
        apiAccountsBySplitPlan(currentFilters),
        apiAccountsByDistributorN1(currentFilters),
        apiAccountsByDistributorN2(currentFilters),
        apiAccountsByAgent(currentFilters),
        // Previous period requests
        apiAccountSnapshots(previousFilters),
        apiDailyTransactions(previousFilters),
        apiAccountsByType(previousFilters),
        apiAccountsByMcc(previousFilters),
        apiAccountsByCategory(previousFilters),
        apiAccountsBySplitPlan(previousFilters),
        apiAccountsByDistributorN1(previousFilters),
        apiAccountsByDistributorN2(previousFilters),
        apiAccountsByAgent(previousFilters)
      ]);
      
      // Process and set data
      const processResults = (data) => {
        const results = data?.results || data || [];
        return Array.isArray(results) ? results : [];
      };
      
      setAccountSnapshots({
        current: processResults(currentSnapshots),
        previous: processResults(previousSnapshots)
      });
      
      setTransactionData({
        current: processResults(currentTransactions),
        previous: processResults(previousTransactions)
      });
      
      setAccountsByType({
        current: processResults(currentByType),
        previous: processResults(previousByType)
      });
      
      setAccountsByMcc({
        current: processResults(currentByMcc),
        previous: processResults(previousByMcc)
      });
      
      setAccountsByCategory({
        current: processResults(currentByCategory),
        previous: processResults(previousByCategory)
      });
      
      setAccountsBySplitPlan({
        current: processResults(currentBySplitPlan),
        previous: processResults(previousBySplitPlan)
      });
      
      setAccountsByDistributorN1({
        current: processResults(currentByDistN1),
        previous: processResults(previousByDistN1)
      });
      
      setAccountsByDistributorN2({
        current: processResults(currentByDistN2),
        previous: processResults(previousByDistN2)
      });
      
      setAccountsByAgent({
        current: processResults(currentByAgent),
        previous: processResults(previousByAgent)
      });
      
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Erro ao carregar estatísticas. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [dateRange, previousPeriod]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Calculate summary totals
  const accountsSummary = useMemo(() => {
    const currentTotal = accountSnapshots.current.length > 0 
      ? accountSnapshots.current[accountSnapshots.current.length - 1]?.total_accounts || 0
      : 0;
    const previousTotal = accountSnapshots.previous.length > 0
      ? accountSnapshots.previous[accountSnapshots.previous.length - 1]?.total_accounts || 0
      : 0;
    return { current: currentTotal, previous: previousTotal };
  }, [accountSnapshots]);

  const transactionsSummary = useMemo(() => {
    const sumField = (data, field) => data.reduce((sum, item) => sum + (item[field] || 0), 0);
    return {
      totalTransactions: {
        current: sumField(transactionData.current, 'total_transactions'),
        previous: sumField(transactionData.previous, 'total_transactions')
      },
      succeededTransactions: {
        current: sumField(transactionData.current, 'succeeded_transactions'),
        previous: sumField(transactionData.previous, 'succeeded_transactions')
      },
      totalAmount: {
        current: sumField(transactionData.current, 'total_amount'),
        previous: sumField(transactionData.previous, 'total_amount')
      }
    };
  }, [transactionData]);

  // Memoize the financial volume data transformation
  const financialVolumeData = useMemo(() => ({
    current: transactionData.current.map(item => ({
      ...item,
      total_amount_formatted: (item.total_amount || 0) / 100
    })),
    previous: transactionData.previous.map(item => ({
      ...item,
      total_amount_formatted: (item.total_amount || 0) / 100
    }))
  }), [transactionData]);

  const formatCurrency = (value) => {
    return (value / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className='Statistics'>
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Breadcrumb
          style={{
            margin: "16px 0",
          }}
        >
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">Estatísticas</h2>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={setDateRange}
                format={{ format: 'DD/MM/YYYY', type: 'mask' }}
                style={{ width: 280 }}
                placeholder={['Data inicial', 'Data final']}
                allowClear={false}
              />
            </div>
          </div>
          <Divider />
          <Breadcrumb.Item>Sistema</Breadcrumb.Item>
          <Breadcrumb.Item>Estatísticas</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          {error && (
            <Alert
              message="Erro ao carregar dados"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}
          
          <Spin spinning={loading} tip="Carregando estatísticas...">
            <div className="statistics-container">
              {/* Summary Cards */}
              <div className="statistics-section">
                <h3 className="section-title">Resumo do Período</h3>
                <Row gutter={[24, 24]}>
                  <Col xs={24} sm={12} lg={6}>
                    <StatsSummaryCard
                      title="Total de Contas"
                      currentValue={accountsSummary.current}
                      previousValue={accountsSummary.previous}
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <StatsSummaryCard
                      title="Total de Transações"
                      currentValue={transactionsSummary.totalTransactions.current}
                      previousValue={transactionsSummary.totalTransactions.previous}
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <StatsSummaryCard
                      title="Transações Aprovadas"
                      currentValue={transactionsSummary.succeededTransactions.current}
                      previousValue={transactionsSummary.succeededTransactions.previous}
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <StatsSummaryCard
                      title="Volume Total"
                      currentValue={transactionsSummary.totalAmount.current}
                      previousValue={transactionsSummary.totalAmount.previous}
                      loading={loading}
                      formatter={formatCurrency}
                      prefix="R$ "
                    />
                  </Col>
                </Row>
              </div>

              {/* Line Charts Section */}
              <div className="statistics-section">
                <h3 className="section-title">Gráficos de Evolução</h3>
                <Row gutter={[24, 24]}>
                  <Col xs={24} lg={12}>
                    <StatisticsLineChart
                      title="Evolução de Contas"
                      currentPeriodData={accountSnapshots.current}
                      previousPeriodData={accountSnapshots.previous}
                      dataKey="total_accounts"
                      dataLabel="Contas"
                      loading={loading}
                      formatValue={(val) => val.toLocaleString('pt-BR')}
                      yAxisFormatter={(val) => val.toLocaleString('pt-BR')}
                    />
                  </Col>
                  <Col xs={24} lg={12}>
                    <StatisticsLineChart
                      title="Volume de Transações"
                      currentPeriodData={transactionData.current}
                      previousPeriodData={transactionData.previous}
                      dataKey="total_transactions"
                      dataLabel="Transações"
                      loading={loading}
                      formatValue={(val) => val.toLocaleString('pt-BR')}
                      yAxisFormatter={(val) => val.toLocaleString('pt-BR')}
                    />
                  </Col>
                </Row>
                <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                  <Col xs={24} lg={12}>
                    <StatisticsLineChart
                      title="Transações Aprovadas"
                      currentPeriodData={transactionData.current}
                      previousPeriodData={transactionData.previous}
                      dataKey="succeeded_transactions"
                      dataLabel="Aprovadas"
                      loading={loading}
                      formatValue={(val) => val.toLocaleString('pt-BR')}
                      yAxisFormatter={(val) => val.toLocaleString('pt-BR')}
                    />
                  </Col>
                  <Col xs={24} lg={12}>
                    <StatisticsLineChart
                      title="Volume Financeiro (R$)"
                      currentPeriodData={financialVolumeData.current}
                      previousPeriodData={financialVolumeData.previous}
                      dataKey="total_amount_formatted"
                      dataLabel="Volume"
                      loading={loading}
                      formatValue={(val) => `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                      yAxisFormatter={(val) => `R$ ${val.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
                    />
                  </Col>
                </Row>
              </div>

              {/* Tables Section */}
              <div className="statistics-section">
                <h3 className="section-title">Detalhamento por Segmento</h3>
                <Row gutter={[24, 24]}>
                  <Col xs={24} lg={12}>
                    <StatisticsTable
                      title="Por Tipo de Conta"
                      currentPeriodData={accountsByType.current}
                      previousPeriodData={accountsByType.previous}
                      groupByKey="account_type"
                      groupByLabel="Tipo de Conta"
                      countKey="count"
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} lg={12}>
                    <StatisticsTable
                      title="Por MCC"
                      currentPeriodData={accountsByMcc.current}
                      previousPeriodData={accountsByMcc.previous}
                      groupByKey="mcc"
                      groupByLabel="MCC"
                      countKey="count"
                      loading={loading}
                    />
                  </Col>
                </Row>
                <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                  <Col xs={24} lg={12}>
                    <StatisticsTable
                      title="Por Categoria"
                      currentPeriodData={accountsByCategory.current}
                      previousPeriodData={accountsByCategory.previous}
                      groupByKey="category"
                      groupByLabel="Categoria"
                      countKey="count"
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} lg={12}>
                    <StatisticsTable
                      title="Por Plano de Split"
                      currentPeriodData={accountsBySplitPlan.current}
                      previousPeriodData={accountsBySplitPlan.previous}
                      groupByKey="split_plan"
                      groupByLabel="Plano de Split"
                      countKey="count"
                      loading={loading}
                    />
                  </Col>
                </Row>
                <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                  <Col xs={24} lg={12}>
                    <StatisticsTable
                      title="Por Distribuidor N1"
                      currentPeriodData={accountsByDistributorN1.current}
                      previousPeriodData={accountsByDistributorN1.previous}
                      groupByKey="distributor"
                      groupByLabel="Distribuidor N1"
                      countKey="count"
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} lg={12}>
                    <StatisticsTable
                      title="Por Distribuidor N2"
                      currentPeriodData={accountsByDistributorN2.current}
                      previousPeriodData={accountsByDistributorN2.previous}
                      groupByKey="distributor"
                      groupByLabel="Distribuidor N2"
                      countKey="count"
                      loading={loading}
                    />
                  </Col>
                </Row>
                <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                  <Col xs={24} lg={12}>
                    <StatisticsTable
                      title="Por Agente"
                      currentPeriodData={accountsByAgent.current}
                      previousPeriodData={accountsByAgent.previous}
                      groupByKey="agent"
                      groupByLabel="Agente"
                      countKey="count"
                      loading={loading}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          </Spin>
        </Content>
      </Layout>
    </div>
  );
};

export default Statistics;
